import { useMemo, useRef } from "react";
import type { RequestState } from "@redux/state/shared-types";

interface UsePageLayoutStateOptions {
  /** Current fetch state from Redux */
  fetching: RequestState;
  /** Treat "idle" as loading state (default: true) */
  idleAsLoading?: boolean;
  /** Always treat "idle" as loading, ignoring idleAsLoading flag (for detail pages) */
  alwaysIdleAsLoading?: boolean;
  /** Keep showing data while re-fetching (for server-side paginated lists) */
  keepDataWhileRefetching?: boolean;
}

interface PageLayoutState {
  /** True when fetching is pending, or idle (if configured) */
  isLoading: boolean;
  /** True when fetching has failed */
  isError: boolean;
  /** True when fetching succeeded */
  isSuccess: boolean;
}

/**
 * Hook to compute page layout state from Redux fetch state.
 * Centralizes the state logic for all page layouts.
 *
 * @example
 * ```tsx
 * const { isLoading, isError, isSuccess } = usePageLayoutState({
 *   fetching,
 *   idleAsLoading: true,
 * });
 * ```
 */
export function usePageLayoutState({
  fetching,
  idleAsLoading = false,
  alwaysIdleAsLoading = false,
  keepDataWhileRefetching = false,
}: UsePageLayoutStateOptions): PageLayoutState {
  const hasLoadedOnce = useRef(false);
  if (fetching === "success") hasLoadedOnce.current = true;

  return useMemo(() => {
    const shouldTreatIdleAsLoading = alwaysIdleAsLoading || idleAsLoading;
    const skipLoading = keepDataWhileRefetching && hasLoadedOnce.current;

    const isLoading = !skipLoading && (fetching === "pending" || (shouldTreatIdleAsLoading && fetching === "idle"));
    const isError = !skipLoading && fetching === "failed";
    const isSuccess = fetching === "success" || skipLoading;

    return { isLoading, isError, isSuccess };
  }, [fetching, idleAsLoading, alwaysIdleAsLoading, keepDataWhileRefetching]);
}
